import { useEffect } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCrud } from '../../../lib/hooks';
import { SttSelectWeekdays } from '../../../sporttia/selectors/SttSelectWeekdays';
import { SttFormTime } from '../../../sporttia/forms/SttFormTime';
import { SttDialog } from '../../../sporttia/dialogs/SttDialog';
import { PERMISSIONS } from '../../../lib/permissions';

type Slot = {
    id?: string;
    weekdays?: number[];
    timeini?: string;
    timeend?: string;
};

type Props = {
    item: Slot;
    onSave: (slot: Slot) => void;
    onDelete: (slot: Slot) => void;
    onClose: () => void;
    loading?: boolean;
};

export default function BookingScheduleSlotDialog({
    item,
    onSave,
    onDelete,
    onClose,
    ...rest
}: Props): React.ReactElement | null {
    const { t } = useTranslation();

    const [slot, setSlot, setProperty] = useCrud<Slot>(item);

    // item cambia. Si el contiene id, cargar los periodos asociados.
    useEffect(() => {
        setSlot(item);
    }, [item]);

    if (!slot) {
        return null;
    }

    return (
        <SttDialog
            open
            onClose={onClose}
            title={slot.id ? t('Slot') : t('CreateTimeSlot')}
            content={
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <SttSelectWeekdays
                            disabled={!!slot.id}
                            selected={slot.weekdays}
                            onChange={setProperty}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        {/* El output de esto es tipo HH:mm:ss así que lo convertimos a HH:mm para el Slot */}
                        <SttFormTime
                            disabled={!!slot.id}
                            name="timeini"
                            caption={t('From')}
                            defVal={slot.timeini}
                            min="00:00:00"
                            max="23:45:00"
                            interval={5}
                            // onChange={({ name, value }: any) => handleChange(({name, value: value.split(":").splice(0,2).join(":")}))}
                            onChange={setProperty}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        {/* El output de esto es tipo HH:mm:ss así que lo convertimos a HH:mm para el Slot */}
                        <SttFormTime
                            disabled={!!slot.id}
                            name="timeend"
                            caption={t('Until')}
                            defVal={slot.timeend}
                            min="00:00:00"
                            max="23:45:00"
                            interval={5}
                            // onChange={({ name, value }: any) => handleChange(({name, value: value.split(":").splice(0,2).join(":")}))}
                            onChange={setProperty}
                        />
                    </Grid>
                </Grid>
            }
            buttons={[
                !slot.id
                    ? {
                          type: 'save' as const,
                          onClick: () => onSave(slot),
                          permission: PERMISSIONS.FIXED_BOOKING.MODIFY,
                          loading: rest.loading,
                      }
                    : undefined,
                slot.id
                    ? {
                          type: 'delete' as const,
                          onClick: () => onDelete(slot),
                          permission: PERMISSIONS.FIXED_BOOKING.MODIFY,
                      }
                    : undefined,
            ].filter(
                (button): button is NonNullable<typeof button> =>
                    button !== undefined,
            )}
        />
    );
}
